import { yupResolver } from '@hookform/resolvers/yup';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Card,
	CardActions,
	Grid,
	Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { ONOFF_BOOLEAN, PAUSEORMOVEWAITING } from '@/common/constants/common';
import { LoginAccountEntity } from '@/api/DTO/login-account/loginAccount.interface';
import {
	useGetLoginAccountQuery,
	useUpdateProgramAppSettingsMutation,
} from '@/api/login-account/loginAccountApi';
import { API_DATE_FORMAT, afterTodayDate } from '@/utils/dateHelper';
import { programAppLoginTabPath } from './ProgramAppLoginTab';

// pauseOrMoveWaiting: 0 = N/A, 1 = Pause enrolment, 2 = Move to waiting list
const needsEndDate = (value: unknown) => value == 1 || value == 2;

interface Props {
	entity: LoginAccountEntity;
}

export default function ProgramAppSettingsPage({ entity }: Props) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { admissionRequestId, familyId } = useParams();
	const id = (entity === 'member' ? admissionRequestId : familyId) || '';
	const target = { entity, id };

	const { data: accountResponse } = useGetLoginAccountQuery(target, {
		skip: !id,
		refetchOnMountOrArgChange: true,
	});
	const account = accountResponse?.data;

	const [updateSettings] = useUpdateProgramAppSettingsMutation();

	const formSchema = Yup.object().shape({
		notificationProgramApp: Yup.boolean()
			.nullable()
			.required(t('errorMessage.pleaseSelect')),
		pauseOrMoveWaiting: Yup.string()
			.nullable()
			.required(t('errorMessage.pleaseSelect')),
		endDateOfPauseEnrollment: Yup.string()
			.nullable()
			.when('pauseOrMoveWaiting', {
				is: needsEndDate,
				then: (schema) => schema.required(t('errorMessage.required')),
			})
			.test(afterTodayDate(t('errorMessage.dateMustBeAfterToday'))),
	});

	// RadioGroup locks into uncontrolled mode if its first value is undefined,
	// so start with null to let the later reset() prefill the radios.
	const methods = useForm<any>({
		defaultValues: {
			notificationProgramApp: null,
			pauseOrMoveWaiting: null,
			endDateOfPauseEnrollment: null,
		},
		resolver: yupResolver(formSchema),
	});
	const pauseOrMoveWaiting = methods.watch('pauseOrMoveWaiting');

	useEffect(() => {
		if (account) {
			methods.reset({
				notificationProgramApp: account.notificationProgramApp ?? null,
				pauseOrMoveWaiting: account.pauseOrMoveWaiting ?? null,
				endDateOfPauseEnrollment: account.endDateOfPauseEnrollment ?? null,
			});
		}
	}, [account]);

	const handleClose = () => {
		navigate(programAppLoginTabPath(entity, id), {
			replace: true,
		});
	};

	const onSubmit: SubmitHandler<any> = async (data) => {
		const response = await updateSettings({
			...target,
			notificationProgramApp: String(data.notificationProgramApp) === 'true',
			pauseOrMoveWaiting: Number(data.pauseOrMoveWaiting),
			endDateOfPauseEnrollment: data.endDateOfPauseEnrollment || null,
		});
		if ('data' in response) {
			enqueueSnackbar(t('loginAccount.updateSettingsSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<form onSubmit={methods.handleSubmit(onSubmit)}>
				<Typography variant='h4'>
					{entity === 'member'
						? t('drawerListItems.editMember')
						: t('drawerListItems.editFamilyMember')}
				</Typography>
				<Card sx={{ padding: '24px', marginTop: 3 }}>
					<Typography
						variant='body1'
						sx={{ fontWeight: 'bold' }}
						marginBottom={2}
					>
						{t('loginAccount.programAppSettings')}
					</Typography>
					{account?.sharedWithKat && (
						<Alert severity='warning' sx={{ marginBottom: 2 }}>
							{t('loginAccount.sharedWithKat')}
						</Alert>
					)}
					<Grid container spacing={3} marginTop={0} marginBottom={3}>
						<Grid size={12}>
							<Typography variant='body2' color='common.neutral40'>
								{t('member.notificationProgramApp') + '*'}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='notificationProgramApp'
								row
								options={ONOFF_BOOLEAN}
							/>
						</Grid>
						<Grid size={6}>
							<Typography variant='body2' color='common.neutral40'>
								{t('member.pauseEnrolmentMoveWaitingList') + '*'}
							</Typography>
							<AppRadioGroup
								control={methods.control}
								name='pauseOrMoveWaiting'
								row
								options={PAUSEORMOVEWAITING}
							/>
						</Grid>
						<Grid size={6}>
							<AppDatePicker
								minimumDate={dayjs()
									.add(1, 'day')
									.startOf('day')
									.format(API_DATE_FORMAT)}
								control={methods.control}
								name='endDateOfPauseEnrollment'
								label={
									t('member.endDatePauseEnrolmentWaitingList') +
									(needsEndDate(pauseOrMoveWaiting) ? '*' : '')
								}
							/>
						</Grid>
					</Grid>
					<CardActions sx={{ justifyContent: 'end', padding: '24px' }}>
						<Button onClick={handleClose}>{t('button.cancel')}</Button>
						<Button
							type='submit'
							variant='contained'
							endIcon={<CheckCircleOutlineIcon />}
						>
							{t('button.complete')}
						</Button>
					</CardActions>
				</Card>
			</form>
		</Box>
	);
}
